import { useState } from "react";
import { Box, Button, Divider, Grid, IconButton, MenuItem, TextField, Typography } from "@mui/material";
import InvoicePdfTemplate from "../components/InvoiceGenerator/InvoicePdfTemplate";
import { STORAGE_KEYS } from "../hooks/useLocalStorage";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import useInvoice from "../hooks/useInvoice";
import InvoiceUtils, {
  AVAILABLE_CURRENCIES,
  formatInvoiceIssueDate,
  parseInvoiceIssueDate,
  prefillReplacesExistingData,
} from "../utils/InvoiceUtils";
import { AppDialog } from "../components/AppDialog";
import OpsPanel from "../components/OpsPanel";
import { ButtonComponent } from "../components/ButtonComponent";
import "../styles/InvoiceGenerator.css";
import { NumericFormat } from "react-number-format";
import { NUMERIC_SX, Token } from "../theme";
import { DatePickerComponent, DatePickerWrapper } from "../components/AppToolbar";
import { createPortal } from "react-dom";

export default function InvoiceGenerator() {
  const { formData, isPrefilling, prefillForUser, addItem, removeItem, updateItem, updateField } = useInvoice();
  const [confirmPrefill, setConfirmPrefill] = useState(false);

  const handlePrefillClick = () => {
    if (prefillReplacesExistingData(formData)) {
      setConfirmPrefill(true);
      return;
    }
    void prefillForUser();
  };

  const { subtotal, taxRate, taxAmount, total } = InvoiceUtils.calculateTotals(formData.items);

  const isGenerateDisabled = !formData.invoiceNumber;

  const handlePrint = () => {
    const currentInvoiceNumber = formData.invoiceNumber ?? "1";
    localStorage.setItem(STORAGE_KEYS.LAST_INVOICE_NUMBER, currentInvoiceNumber);
    window.print();
    updateField("invoiceNumber", String(Number(currentInvoiceNumber) + 1));
  };

  return (
    <>
      <AppDialog
        open={confirmPrefill}
        onClose={() => setConfirmPrefill(false)}
        title="Replace invoice data?"
        maxWidth="sm"
        content={
          <Typography sx={{ color: Token.textSecondary }}>
            Prefill replaces the beneficiary, the line items, and the bill-to text with your profile and this month&apos;s hours.
          </Typography>
        }
        buttons={[
          {
            label: "Replace",
            onClick: () => {
              setConfirmPrefill(false);
              void prefillForUser();
            },
          },
        ]}
      />
      {createPortal(
        <div id="invoice-print-area">
          <InvoicePdfTemplate data={formData} />
        </div>,
        document.body,
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 1.5,
        }}
      >
        <Typography variant="h1" sx={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.02em", color: Token.textPrimary }}>
          Invoice generator
        </Typography>
        <Button
          variant="contained"
          disableElevation
          disabled={isGenerateDisabled}
          onClick={handlePrint}
          sx={{ backgroundColor: Token.actionPrimary, color: Token.textOnAction }}
        >
          Generate Invoice
        </Button>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.15fr) minmax(0, 1fr)" },
          gap: 1.5,
          alignItems: "start",
        }}
      >
        <Grid container direction="column" gap={2}>
          {/*  Invoice Details  */}
          <OpsPanel title="Invoice Details">
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <NumericFormat
                  fullWidth
                  customInput={TextField}
                  decimalScale={0}
                  allowNegative={false}
                  label="Invoice Number"
                  value={formData.invoiceNumber}
                  onValueChange={(value) => updateField("invoiceNumber", value.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePickerWrapper>
                  <DatePickerComponent
                    views="day"
                    label="Issue Date"
                    inputFormat="dd/MM/yyyy"
                    value={parseInvoiceIssueDate(formData.issueDate)}
                    onChange={(value) => updateField("issueDate", value ? formatInvoiceIssueDate(value) : "")}
                    style={{ marginRight: 0, width: "100%" }}
                  />
                </DatePickerWrapper>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Currency"
                  value={formData.currency}
                  onChange={(e) => updateField("currency", e.target.value)}
                >
                  {AVAILABLE_CURRENCIES.map((currency) => (
                    <MenuItem key={currency} value={currency}>
                      {currency}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </OpsPanel>

          {/* From */}
          <OpsPanel
            title="From"
            panelMenu={
              <Box sx={{ display: "flex", justifyContent: "flex-start", marginRight: "8px", maxHeight: "35px" }}>
                <ButtonComponent
                  style={{
                    borderColor: Token.borderDefault,
                    backgroundColor: Token.surfaceCard,
                    color: Token.textPrimary,
                  }}
                  onClick={handlePrefillClick}
                  disabled={isPrefilling}
                  text={"Prefill invoice data"}
                  debounce={true}
                  variant="outlined"
                />
              </Box>
            }
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Beneficiary Name"
                  value={formData.from.beneficiaryName}
                  onChange={(e) => updateField("from", { ...formData.from, beneficiaryName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Country"
                  value={formData.from.country}
                  onChange={(e) => updateField("from", { ...formData.from, country: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Address Line 1"
                  value={formData.from.addressLine1}
                  onChange={(e) => updateField("from", { ...formData.from, addressLine1: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Address Line 2"
                  value={formData.from.addressLine2}
                  onChange={(e) => updateField("from", { ...formData.from, addressLine2: e.target.value })}
                />
              </Grid>
            </Grid>
          </OpsPanel>

          {/* Bill To */}
          <OpsPanel title="Bill To">
            <TextField
              fullWidth
              label="Bill To Description"
              multiline
              rows={3}
              value={formData.billTo}
              onChange={(e) => updateField("billTo", e.target.value)}
            />
          </OpsPanel>

          {/* Items */}
          <OpsPanel title="Items">
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1, px: 1 }}>
              <Grid item xs={5}>
                <Typography
                  sx={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".08em", fontWeight: 600, color: Token.textTertiary }}
                >
                  Description
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography
                  sx={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".08em", fontWeight: 600, color: Token.textTertiary }}
                >
                  Quantity
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography
                  sx={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".08em", fontWeight: 600, color: Token.textTertiary }}
                >
                  Rate
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography
                  sx={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".08em", fontWeight: 600, color: Token.textTertiary }}
                >
                  Amount
                </Typography>
              </Grid>
              <Grid item xs={1} />
            </Grid>
            <Divider />

            {formData.items.map((item, index) => (
              <Grid container spacing={1} alignItems="center" key={index} sx={{ mt: 1, px: 1 }}>
                <Grid item xs={5}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) => updateItem(index, "description", e.target.value)}
                  />
                </Grid>
                <Grid item xs={2}>
                  <NumericFormat
                    fullWidth
                    customInput={TextField}
                    size="small"
                    inputMode="numeric"
                    placeholder="Quantity"
                    value={item.quantity}
                    decimalScale={0}
                    allowNegative={false}
                    allowLeadingZeros={false}
                    isAllowed={(digits) => digits.value.length <= 8}
                    onValueChange={(digits) => {
                      updateItem(index, "quantity", digits.floatValue ?? 0);
                    }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <NumericFormat
                    fullWidth
                    customInput={TextField}
                    size="small"
                    inputMode="decimal"
                    placeholder="Rate"
                    value={item.unitPrice}
                    decimalScale={3}
                    allowNegative={false}
                    isAllowed={(digits) => digits.value.length <= 10}
                    allowLeadingZeros={false}
                    onValueChange={(digits) => {
                      updateItem(index, "unitPrice", digits.floatValue ?? 0);
                    }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <Typography sx={{ ...NUMERIC_SX, color: Token.textPrimary }}>
                    {InvoiceUtils.formatCurrency(item.quantity * Number(item.unitPrice), formData.currency)}
                  </Typography>
                </Grid>
                <Grid item xs={1}>
                  {formData.items.length > 1 && (
                    <IconButton size="small" onClick={() => removeItem(index)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            ))}

            <ButtonComponent text="Add Item" icon={<AddIcon />} onClick={addItem} style={{ marginTop: "1rem", color: Token.textPrimary }} />

            {/* Total */}
            <Divider sx={{ mt: 2, mb: 1 }} />
            <Grid container justifyContent="flex-end">
              <Grid item xs={12} md={4}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                  <Typography sx={{ color: Token.textSecondary }}>Subtotal</Typography>
                  <Typography sx={{ ...NUMERIC_SX, color: Token.textPrimary }}>
                    {InvoiceUtils.formatCurrency(subtotal, formData.currency)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                  <Typography sx={{ color: Token.textSecondary }}>Tax ({taxRate}%)</Typography>
                  <Typography sx={{ ...NUMERIC_SX, color: Token.textPrimary }}>
                    {InvoiceUtils.formatCurrency(taxAmount, formData.currency)}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                  <Typography sx={{ fontWeight: 700, color: Token.textPrimary }}>Total</Typography>
                  <Typography sx={{ ...NUMERIC_SX, fontWeight: 700, fontSize: 15, color: Token.textPrimary }}>
                    {InvoiceUtils.formatCurrency(total, formData.currency)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </OpsPanel>

          {/* Terms */}
          <OpsPanel title="Terms">
            <TextField
              fullWidth
              label="Payment Terms"
              multiline
              rows={4}
              value={formData.paymentTerms}
              onChange={(e) => updateField("paymentTerms", e.target.value)}
            />
          </OpsPanel>
        </Grid>

        <Box sx={{ display: { xs: "none", lg: "block" } }}>
          <Box
            sx={{
              border: `1px solid ${Token.borderDefault}`,
              borderRadius: "8px",
              overflow: "hidden",
              backgroundColor: Token.surfaceCard,
            }}
          >
            <Box
              sx={{
                height: 36,
                px: 1.5,
                borderBottom: `1px solid ${Token.borderSubtle}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: Token.textPrimary }}>Preview</span>
              <span style={{ fontSize: 11.5, color: Token.textTertiary }}>Letter · PDF</span>
            </Box>
            <InvoicePdfTemplate data={formData} surface="screen" />
          </Box>
          <Box sx={{ fontSize: 11.5, color: Token.textTertiary, mt: 1, px: 0.5 }}>
            Only approved hours are billed. Anything still pending approval is left out.
          </Box>
        </Box>
      </Box>
    </>
  );
}
